import { Injectable } from "@angular/core";
import { CoreLogService } from "./log.service";
import { GeocodeResponse, GeocodeRequest } from "../shared/models/geocoding.model";
import { CircularInjectionHelper } from "./circularInjectionHelper";
import { ApiResponse } from "./YourAppServer/YourAppServerApi.service";


export class Address {
    public street: string;
    public streetNumber: string;
    public postalCode: string;
    public city: string;
    public country: string;
}

type CacheEntry = {
    timestamp: number;
    data: GeocodeResponse;
}

@Injectable({
    providedIn: "root",
})
export class GeocodingService {
    private readonly endpoint = "geocoding/geocode";
    private readonly TTL = 60000; // 1 Minute
    private cache: Map<string, CacheEntry>;

    constructor(
        private _coreLogService: CoreLogService,
        private _objObsInjectHelper: CircularInjectionHelper
    ) {
        this.cache = new Map();
    }

    public async geocode(address: Address): Promise<GeocodeResponse> {
        const me = this;
        const cacheKey = me.getCacheKey(address);

        if (me.cache.has(cacheKey)) {
            const cacheEntry = me.cache.get(cacheKey);
            if (me.isCacheValid(cacheEntry)) {
                return cacheEntry.data;
            } else {
                me.cache.delete(cacheKey);
            }
        }

        try {
            const geocodeResponse = await me.fetchGeocodeFromService(address);
            me.cache.set(cacheKey, {
                data: geocodeResponse,
                timestamp: Date.now()
            });

            return geocodeResponse;
        } catch (error) {
            throw error; // Rethrow to handle in the frontend
        }
    }


    private getCacheKey(address: Address): string {
        return `${address.street.toLowerCase()}_${address.streetNumber.toLowerCase()}_${address.postalCode}_${address.city.toLowerCase()}`;
    }

    private isCacheValid(entry: { data: GeocodeResponse, timestamp: number }): boolean {
        return (Date.now() - entry.timestamp) < this.TTL;
    }

    private async fetchGeocodeFromService(address: Address): Promise<GeocodeResponse> {
        try {
            let response = await this.sendRequest(address);

            if (response.OK) {
                this._coreLogService.debug("GeocodingService.geocode", "RequestService (response OK)", response);
                return response.Data;
            } else {
                this._coreLogService.error("GeocodingService.geocode", "RequestService (response not OK)", response);
                throw new Error(response.HttpResponse.codeText);
            }
        } catch (error) {
            this._coreLogService.error("GeocodingService", "geocode", error);
            throw error;
        }
    }

    private async sendRequest(address: Address): Promise<ApiResponse<GeocodeResponse>> {
        return this._objObsInjectHelper.yourAppServer.Api.RequestService(
            GeocodeResponse,
            this.endpoint,
            this.toGeocodeRequest(address)
        )
    }

    private toGeocodeRequest(address: Address): GeocodeRequest {
        return {
            Street: address.street,
            StreetNumber: address.streetNumber,
            PostalCode: address.postalCode,
            City: address.city,
            Country: address.country,
        };
    }
}